/**
 * Reading and writing the business profile.
 *
 * The write goes through `businessService` inside a real Dexie transaction —
 * this hook does no business deciding of its own. It exists to assemble the
 * query, and to invalidate what a profile change dirties.
 *
 * WHY A SAVE INVALIDATES THE WHOLE INVENTORY
 * ------------------------------------------
 * A profile edit changes the LETTERHEAD, and the letterhead is read live at
 * print time by `businessReader` rather than snapshotted onto the document. So
 * editing the name or the GSTIN changes what every open document would print
 * next, and any cached document answer is now describing a different company.
 * Narrowing this to "just the businesses" is how a screen ends up printing one
 * company's name on another's invoice.
 *
 * "NO BUSINESS YET" IS A STATE, NOT A FAILURE
 * -------------------------------------------
 * A fresh install has no business row, and this screen is where one is created.
 * The query therefore returns `null` and stays enabled, rather than erroring —
 * an error state on the one screen that fixes it is a dead end.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { createBusiness, updateBusiness } from '@data/services/businessService';
import {
  AttachmentError,
  deleteBusinessImage,
  saveBusinessImage,
  type BusinessImageKind,
} from '@data/services/attachmentService';
import { setActiveBusinessId } from '@data/services/businessSession';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import {
  BUSINESSES_QUERY_ROOT,
  GATE_QUERY_KEY,
  INVENTORY_QUERY_ROOTS,
  useActiveBusiness,
} from '@features/documents/useDocuments';
import type { Business } from '@data/db/types';
import { ulid } from '@platform/ids';
import type { ProfileSubmission } from './businessProfileForm';

/** Root of the profile query, invalidated after every save. */
export const BUSINESS_PROFILE_QUERY_ROOT = ['businessProfile'] as const;

export interface UseBusinessProfileResult {
  /** Still resolving which company is open. */
  readonly loading: boolean;
  /** The company being worked in, or null when none has been created yet. */
  readonly business: Business | null;
  /** True when a company exists and the form should be in EDIT mode. */
  readonly exists: boolean;
}

/**
 * The active business row, in full.
 *
 * The resolved id is part of the query KEY, not just an argument inside
 * `queryFn`, for the reason `useParties` gives: a key that omits the company
 * serves the previous company's cached answer after a switch, which here would
 * be one company's GSTIN on another company's profile form.
 */
export function useBusinessProfile(): UseBusinessProfileResult {
  const active = useActiveBusiness();
  const businessId = active.data ?? null;

  const query = useQuery({
    queryKey: [...BUSINESS_PROFILE_QUERY_ROOT, businessId],
    queryFn: async (): Promise<Business | null> => {
      if (businessId === null) return null;
      return (await getDb().businesses.get(businessId)) ?? null;
    },
    // Enabled even with no business, so a fresh install renders the CREATE form
    // rather than a spinner that never resolves.
    enabled: active.isSuccess,
  });

  return {
    loading: active.isLoading || (active.isSuccess && query.isLoading),
    business: query.data ?? null,
    exists: query.data !== undefined && query.data !== null,
  };
}

export interface SaveProfileVariables {
  /** null to CREATE the first business; an id to EDIT that one. */
  readonly businessId: string | null;
  readonly profile: ProfileSubmission;
}

/**
 * Create or update the business.
 *
 * A create ALSO makes the new company the active one. Without that, the app
 * would save a business and then keep showing "no company selected", and the
 * user's first action would appear to do nothing.
 */
export function useSaveBusinessProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, profile }: SaveProfileVariables): Promise<Business> => {
      const db = getDb();
      const now = new Date().toISOString();
      const deviceId = await currentDeviceId(db);

      // `settings` joins the scope because a create writes the active-company
      // preference in the same transaction; leaving it out would commit that
      // write outside the transaction that created the row it points at.
      return db.transaction('rw', db.businesses, db.settings, db.syncOutbox, async () => {
        const saved =
          businessId === null
            ? await createBusiness(db as never, { ...profile, now, deviceId })
            : await updateBusiness(db as never, { ...profile, id: businessId, now, deviceId });

        if (businessId === null) await setActiveBusinessId(db, saved.id);
        return saved;
      });
    },
    onSuccess: async () => {
      // A new company changes the MEANING of every screen, so this is the
      // business switch's invalidation list, not a shorter one.
      await queryClient.invalidateQueries({ queryKey: GATE_QUERY_KEY, exact: true });
      await queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_ROOT });
      await queryClient.invalidateQueries({ queryKey: BUSINESS_PROFILE_QUERY_ROOT });
      for (const root of INVENTORY_QUERY_ROOTS) {
        await queryClient.invalidateQueries({ queryKey: root });
      }
    },
  });
}
export interface UploadBusinessImageVariables {
  readonly businessId: string;
  readonly kind: BusinessImageKind;
  readonly file: File;
}

/**
 * Store a logo or signature and point the business at it.
 *
 * AN IMMEDIATE, SEPARATE ACTION - not part of saving the profile form.
 *
 * The bytes are written the moment a file is chosen, and only the POINTER waits
 * for Save. That is deliberate: writing the image into the same save as a GSTIN
 * edit means a half-typed profile can hold a logo nobody meant to commit, and it
 * makes "remove the logo" a mode the form has to represent. A picked image is a
 * complete action in itself.
 *
 * THE POINTER IS WRITTEN HERE, IN THE SAME TRANSACTION AS THE BYTES.
 *
 * The obvious alternative - store the blob, return the id, let the form save it
 * later - can leave a business pointing at an attachment that was never written,
 * which prints a broken image on every invoice with no error to explain it.
 */
export function useUploadBusinessImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      businessId,
      kind,
      file,
    }: UploadBusinessImageVariables): Promise<string> => {
      const db = getDb();
      const now = new Date().toISOString();

      return db.transaction('rw', db.businesses, db.attachments, async () => {
        const field = kind === 'LOGO' ? 'logoAttachmentId' : 'signatureAttachmentId';

        // Read, mutate, put - NOT `businesses.update(id, { field: undefined })`.
        // A partial update with an undefined value is the same "absent means
        // leave it alone" ambiguity that makes merge semantics confusing
        // everywhere else, and whether Dexie drops the key or writes undefined
        // is not something to depend on for a field whose whole purpose is to be
        // empty.
        const row = await db.businesses.get(businessId);
        if (!row) {
          throw new AttachmentError('That company no longer exists, so the image was not changed.');
        }

        const id = ulid();
        await saveBusinessImage(db as never, { businessId, kind, file, now, id });

        // The row being replaced is now unreachable. Left behind, every logo
        // change would strand a copy of the old image forever, in a table
        // nothing ever prunes.
        const stale = row[field];
        await db.businesses.put({ ...row, [field]: id, updatedAt: now });

        if (stale && stale !== id) await deleteBusinessImage(db as never, businessId, stale);

        return id;
      });
    },
    onSuccess: async () => {
      // The letterhead is read live at print time, so a cached identity is now
      // describing a different company - the same reason a profile save
      // invalidates the inventory.
      await queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_ROOT });
      await queryClient.invalidateQueries({ queryKey: BUSINESS_PROFILE_QUERY_ROOT });
    },
  });
}

export interface RemoveBusinessImageVariables {
  readonly businessId: string;
  readonly kind: BusinessImageKind;
}

/**
 * Remove a logo or signature: the pointer AND the bytes.
 *
 * Both, because a cleared pointer with the row still stored is a blob nothing
 * can reach, and a removed row with the pointer left behind is a business whose
 * letterhead prints a broken image on every invoice.
 */
export function useRemoveBusinessImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, kind }: RemoveBusinessImageVariables): Promise<void> => {
      const db = getDb();

      return db.transaction('rw', db.businesses, db.attachments, async () => {
        const field = kind === 'LOGO' ? 'logoAttachmentId' : 'signatureAttachmentId';
        const row = await db.businesses.get(businessId);
        if (!row) {
          throw new AttachmentError('That company no longer exists, so the image was not removed.');
        }
        const current = row[field];
        // The key is DELETED, not set to undefined, so the stored row says what
        // it means. See `useUploadBusinessImage` for why this is a put.
        const { [field]: _cleared, ...rest } = row;
        void _cleared;
        await db.businesses.put({ ...rest, updatedAt: new Date().toISOString() });
        await deleteBusinessImage(db as never, businessId, current);
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_ROOT });
      await queryClient.invalidateQueries({ queryKey: BUSINESS_PROFILE_QUERY_ROOT });
    },
  });
}
